// Typed client for the Worker API (contract: engine/src/api.ts). Every call can fail offline;
// callers that write go through the outbox (lib/sync.ts) instead of calling these directly.
import type {
  DictionaryChanges,
  Letter,
  MeResponse,
  NewFlag,
  NotebookDone,
  PaceChange,
  PaceState,
  PushTestResult,
  Recording,
  RecordingMark,
  SharedSession,
  SharedSettings,
  SharedSettingsChanges,
  SharedState,
  SyncPull,
  SyncPush,
  UserInfo,
} from '@valyrio/engine/api';
import type { Flag, FlagStatus, PaceConfig, Proposal, StackSentence } from '@valyrio/engine/types';
import type { SceneExamples, SpeakingState } from '@valyrio/engine/speaking-ladder';
import { takeStressQuery } from '../listencards/takeStress';

export class ApiError extends Error {
  constructor(
    readonly status: number, // 0 = network / offline
    readonly code: string,
    message?: string,
    /** The server's machine-readable reason (ApiError.code in the contract: pos_missing, letter_changed, …). */
    readonly reason?: string,
    /** Seconds from a 429's Retry-After header (the login lock). */
    readonly retryAfter?: number,
  ) {
    super(message ?? code);
    this.name = 'ApiError';
  }
  /** Worth retrying later (offline, server trouble, rate limit). */
  get retryable(): boolean {
    return this.status === 0 || this.status === 408 || this.status === 429 || this.status >= 500;
  }
  /** Not signed in: the app's session ended (login screen) or Cloudflare Access expired (reload). */
  get auth(): boolean {
    return this.status === 401 || this.code === 'access-login';
  }
  /** The Worker says this device has no valid session: show the login screen. */
  get loginRequired(): boolean {
    return this.status === 401 && this.reason === 'login_required';
  }
}

export type Fetch = typeof fetch;

let loginRequiredHandler: (() => void) | null = null;
/** Called whenever any request learns that the session is gone (state/actions.ts shows the login screen). */
export function onLoginRequired(fn: (() => void) | null): void {
  loginRequiredHandler = fn;
}

export interface RequestOptions {
  /** Sent as Idempotency-Key: a resend after a lost response replays the first answer. */
  idempotencyKey?: string;
  /** Yksi puhelin: the partner's id (X-Acting-As): the server records the write as the partner's (api/src/acting.ts). */
  actingAs?: string;
}

export interface ApiClient {
  request<T>(method: string, path: string, body?: unknown, opts?: RequestOptions): Promise<T>;
  /** POST /api/login: sets the session cookie (HttpOnly) and answers like /api/me. */
  login(username: string, password: string): Promise<MeResponse>;
  /** POST /api/logout: ends this device's session (works even when it has already ended). */
  logout(): Promise<{ ok: true }>;
  /** POST /api/me/password (Asetukset → Vaihda salasana). */
  changePassword(current: string, next: string): Promise<{ ok: true }>;
  me(): Promise<MeResponse>;
  patchMe(body: Partial<Pick<UserInfo, 'uiLang' | 'theme' | 'notebookTasks' | 'coach'>>): Promise<MeResponse>;
  sync(push: SyncPush): Promise<SyncPull>;
  /** GET /api/sync: a pull without a push (the next pages of a paged pull). */
  syncPage(since: string | null, cursor: string, lettersSeenAt?: string | null): Promise<SyncPull>;
  shared(): Promise<SharedState>;
  dictionaryChanges(since: string): Promise<DictionaryChanges>;
  pace(): Promise<PaceState>;
  /** The admin sets the pace for both (applied at once; 403 for the other person). */
  setPace(pace: PaceConfig): Promise<PaceChange>;
  /** The admin sets the shared reminder time (403 for the other person). */
  putSettings(body: Partial<SharedSettings>): Promise<SharedSettings>;
  /** Who set each shared value and when (Asetukset's margin). */
  settingsChanges(): Promise<SharedSettingsChanges>;
  createFlag(flag: NewFlag): Promise<Flag>;
  pushKey(): Promise<{ publicKey: string }>;
  pushSubscribe(sub: PushSubscriptionJSON): Promise<{ ok: true }>;
  pushUnsubscribe(endpoint: string): Promise<{ ok: true }>;
  /** Send the reminder now to my own subscriptions (rate-limited: 429 within a minute). */
  pushTest(delaySeconds?: number): Promise<PushTestResult>;
  // Reads for the sections. Writes go through the outbox (state/actions.ts → queueWrite), except
  // recordings (binary upload, online only) and the shared session result (needs the session id).
  flags(status?: FlagStatus | 'all'): Promise<Flag[]>;
  letters(): Promise<Letter[]>;
  proposals(): Promise<Proposal[]>;
  /** Lausepino: the users' decisions on the sentence stack (approved and skipped sentences). */
  stack(): Promise<StackSentence[]>;
  recordings(target?: string): Promise<Recording[]>;
  /** Recordings whose target starts with `prefix` ("letter:", "scene:got-s3e06-1:", "progress:"). */
  recordingsByPrefix(prefix: string): Promise<Recording[]>;
  /** Every word mark, with its recording's target and recorder (voices together). */
  recordingMarks(): Promise<RecordingMark[]>;
  /** My notebook tasks marked done since a local day (GET /api/notebook?from=). */
  notebook(from: string): Promise<NotebookDone[]>;
  /**
   * `form`: a form recording's form text ("form:<lemmaId>#<slot>" targets, engine/src/form-voices.ts).
   * `actingAs`: Yksi puhelin, the partner's take on this phone (a scene line, the Halloween line).
   */
  uploadRecording(target: string, audio: Blob, form?: string, actingAs?: string): Promise<Recording>;
  /** Trim (2026-10-06): the owner's rendered edit of a recording (PUT /rendered?rev=; 409 stale_edit). */
  uploadRendered(id: string, rev: string, gain: number, audio: Blob): Promise<Recording>;
  sharedSession(): Promise<SharedSession>;
  /** Speaking together (docs/PEDAGOGY.md §3): the pair's ladder step, votes and recent taps. */
  speaking(): Promise<SpeakingState>;
  /** An everyday scene's example takes (ours-… scenes only). */
  sceneExamples(scene: string): Promise<SceneExamples>;
}

export function createApi(fetchImpl: Fetch = (...a) => fetch(...a), base = ''): ApiClient {
  async function request<T>(method: string, path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
    let res: Response;
    // Every write is JSON with Content-Type application/json (the API's CSRF check requires it),
    // even when it has nothing to say: an empty object then.
    const write = method !== 'GET' && method !== 'HEAD';
    const payload = body !== undefined ? body : write ? {} : undefined;
    // A queued recording (recordings/outbox.ts) is the one queued write that is not JSON: its raw
    // audio goes as the body with its own type, as uploadRecording sends it.
    const raw = typeof Blob !== 'undefined' && payload instanceof Blob ? payload : null;
    try {
      res = await fetchImpl(base + path, {
        method,
        credentials: 'same-origin',
        // Access answers an expired session with a redirect to its login page; never follow it.
        redirect: 'manual',
        headers: {
          Accept: 'application/json',
          ...(payload !== undefined ? { 'Content-Type': raw ? raw.type || 'audio/webm' : 'application/json' } : {}),
          ...(opts.idempotencyKey ? { 'Idempotency-Key': opts.idempotencyKey } : {}),
          ...(opts.actingAs ? { 'X-Acting-As': opts.actingAs } : {}),
        },
        body: raw ?? (payload !== undefined ? JSON.stringify(payload) : undefined),
      });
    } catch (err) {
      throw new ApiError(0, 'network', err instanceof Error ? err.message : String(err));
    }
    if (res.type === 'opaqueredirect' || (res.status >= 300 && res.status < 400)) {
      throw new ApiError(401, 'access-login', 'Cloudflare Access session expired');
    }
    const type = res.headers.get('content-type') ?? '';
    if (!res.ok) {
      let code = `http-${res.status}`;
      let message: string | undefined;
      let reason: string | undefined;
      if (type.includes('json')) {
        try {
          const j = (await res.json()) as { error?: string; message?: string; code?: string };
          code = j.error ?? code;
          message = j.message;
          reason = j.code;
        } catch {
          /* ignore */
        }
      }
      const retry = Number(res.headers.get('Retry-After'));
      const e = new ApiError(res.status, code, message, reason, Number.isFinite(retry) && retry > 0 ? retry : undefined);
      if (e.loginRequired) loginRequiredHandler?.();
      throw e;
    }
    if (!type.includes('json')) {
      // An HTML page here means the request never reached the Worker (Access login or SPA fallback).
      throw new ApiError(type.includes('html') ? 401 : 502, type.includes('html') ? 'access-login' : 'bad-response');
    }
    return (await res.json()) as T;
  }

  return {
    request,
    login: (username, password) => request('POST', '/api/login', { username, password }),
    logout: () => request('POST', '/api/logout', {}),
    changePassword: (current, next) => request('POST', '/api/me/password', { current, next }),
    me: () => request('GET', '/api/me'),
    patchMe: (body) => request('PATCH', '/api/me', body),
    sync: (push) => request('POST', '/api/sync', push),
    syncPage: (since, cursor, lettersSeenAt) => {
      const q = new URLSearchParams({ cursor });
      if (since) q.set('since', since);
      if (lettersSeenAt) q.set('lettersSeenAt', lettersSeenAt);
      return request('GET', `/api/sync?${q.toString()}`);
    },
    shared: () => request('GET', '/api/shared'),
    dictionaryChanges: (since) => request('GET', `/api/dictionary/changes?since=${encodeURIComponent(since)}`),
    pace: () => request('GET', '/api/pace'),
    setPace: (pace) => request('POST', '/api/pace', { pace }),
    putSettings: (body) => request('PUT', '/api/settings', body),
    settingsChanges: () => request('GET', '/api/settings/changes'),
    createFlag: (flag) => request('POST', '/api/flags', flag),
    pushKey: () => request('GET', '/api/push/key'),
    pushSubscribe: (sub) => request('POST', '/api/push/subscribe', sub),
    pushUnsubscribe: (endpoint) => request('DELETE', '/api/push/subscribe', { endpoint }),
    pushTest: (delaySeconds = 0) => request('POST', '/api/push/test', { delaySeconds }),
    flags: (status = 'all') => request('GET', `/api/flags?status=${encodeURIComponent(status)}`),
    letters: () => request('GET', '/api/letters'),
    proposals: () => request('GET', '/api/proposals'),
    stack: () => request('GET', '/api/stack'),
    recordings: (target) => request('GET', target ? `/api/recordings?target=${encodeURIComponent(target)}` : '/api/recordings'),
    recordingsByPrefix: (prefix) => request('GET', `/api/recordings?prefix=${encodeURIComponent(prefix)}`),
    recordingMarks: () => request('GET', '/api/recordings/marks'),
    sharedSession: () => request('GET', '/api/shared-session/current'),
    speaking: () => request('GET', '/api/speaking'),
    sceneExamples: (scene) => request('GET', `/api/scene-examples?scene=${encodeURIComponent(scene)}`),
    notebook: (from) => request('GET', `/api/notebook?from=${encodeURIComponent(from)}`),
    uploadRecording(target, audio, form, actingAs) {
      return uploadAudio('POST', recordingUploadPath(target, form, audio), audio, actingAs);
    },
    uploadRendered(id, rev, gain, audio) {
      const q = new URLSearchParams({ rev, gain: String(Math.round(gain * 1000) / 1000) });
      return uploadAudio('PUT', `/api/recordings/${encodeURIComponent(id)}/rendered?${q.toString()}`, audio);
    },
  };

  /** A binary audio upload (recordings and rendered trims, online; a take saved offline goes from the outbox through request(), recordings/outbox.ts). */
  async function uploadAudio(method: 'POST' | 'PUT', path: string, audio: Blob, actingAs?: string): Promise<Recording> {
    let res: Response;
    try {
      res = await fetchImpl(`${base}${path}`, {
        method,
        credentials: 'same-origin',
        redirect: 'manual',
        // iOS Safari records audio/mp4, Chrome/Firefox audio/webm (or ogg): send what we got.
        headers: { 'Content-Type': audio.type || 'audio/webm', Accept: 'application/json', ...(actingAs ? { 'X-Acting-As': actingAs } : {}) },
        body: audio,
      });
    } catch (err) {
      throw new ApiError(0, 'network', err instanceof Error ? err.message : String(err));
    }
    if (res.type === 'opaqueredirect' || (res.status >= 300 && res.status < 400)) throw new ApiError(401, 'access-login');
    if (!res.ok) {
      let code = `http-${res.status}`;
      let reason: string | undefined;
      try {
        const j = (await res.json()) as { error?: string; code?: string };
        code = j.error ?? code;
        reason = j.code;
      } catch {
        /* not json */
      }
      throw new ApiError(res.status, code, undefined, reason);
    }
    return (await res.json()) as Recording;
  }
}

/**
 * POST path of a recording upload (also where a queued one goes, recordings/outbox.ts). `audio`: a
 * word take's stress check measured on the device goes with it (listencards/takeStress.ts).
 */
export function recordingUploadPath(target: string, form?: string, audio?: Blob): string {
  return `/api/recordings?target=${encodeURIComponent(target)}${form !== undefined ? `&form=${encodeURIComponent(form)}` : ''}${takeStressQuery(audio)}`;
}

export const api = createApi();
